import React, { ReactElement, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import classNames from 'classnames/bind';
import { connect, ConnectedProps } from 'react-redux';
import Text from '../../../../../atoms/Text';
import Icon from '../../../../../atoms/Icon';
import EventHeader from '../TopShopperHeader/EventHeader';
import LeaderBoard from '../LeaderBoard/LeaderBoard';
import CircleCard from '../../common/CircleCard';
import Dialog from '../../../../../molecules/Dialog';
import RadioGroup from '../../../../../molecules/RadioGroup';
import ModalContent from '../../common/ModalContent';
import UpcomingEvent from '../UpcomingEvent';
import {
  SELECT_SALE_DAY,
  HEADER_OFFSET,
  SHOP_MORE_URL,
  WEB_HEADER_OFFSET
} from '../../../Constants';
import {
  getEventData,
  getDataForDateDropdown
} from '../../../../../selectors/topShopperPageSelectors';
import getCurrentUser from '../../../utils/userDetails';
import { getDayString } from '../../../utils/TopShopperDateUtils';
import {
  getTopShopperShopMoreHyperlink,
  getTncListData
} from '../../../../../selectors/featureFlagsSelector';
import styles from './index.pcss';

const cx = classNames.bind(styles);

const mapStateToProps = (state: any) => {
  return {
    data: getEventData(state),
    dataForDateDropdown: getDataForDateDropdown(state),
    currentUser: getCurrentUser(),
    shopMoreHyperlink: getTopShopperShopMoreHyperlink(state),
    TncListData: getTncListData(state)
  };
};

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const getEventTypeUI = (type: string, text: string): JSX.Element => {
  return (
    <div className={cx('event-type')}>
      <div className={cx('text')}>
        <Text variant="T3" weight="bold" color="Nero">
          {type}
        </Text>
        <Text variant="T3" weight="bold" color="Nero">
          {` ${text}`}
        </Text>
      </div>
      <div className={cx('horizontal-line')} />
    </div>
  );
};

const EventsAndWinnersPage = (props: PropsFromRedux): ReactElement => {
  const {
    data,
    dataForDateDropdown,
    currentUser,
    onGoBack,
    platform,
    shopMoreHyperlink,
    TncListData
  } = props;
  const { bannerImageURL, pastSlots, activeSlot, upcomingSlots, saleType, description } = data;
  const [showDateDialog, setShowDateDialog] = useState<boolean>(false);
  const [showTnCDialog, setShowTnCDialog] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [windowHash, setWindowHash] = useState<null | string>(null);
  const shopMoreUrl = shopMoreHyperlink || SHOP_MORE_URL;

  useEffect(() => {
    let date = '';
    if (saleType === 'PRE_SALE') {
      date = upcomingSlots[0]?.slot?.date;
    } else if (saleType === 'ACTIVE_SALE') {
      date = activeSlot?.slot?.date;
    } else if (saleType === 'POST_SALE') {
      date = pastSlots[0]?.slot?.date;
    }
    setSelectedDate(date);
  }, []);

  useEffect(() => {
    window.location.hash.length > 2 && setWindowHash(window.location.hash);
  }, [window.location.hash]);

  useEffect(() => {
    try {
      let id = `date-${selectedDate}`;
      if (windowHash && selectedDate) {
        if (window?.location?.hash) {
          id = 'upcoming';
        }
      }
      const node = document.getElementById(`${id}`);
      if (node) {
        const headerOffset = platform === 'website' ? WEB_HEADER_OFFSET : HEADER_OFFSET;
        const nodePosition = node?.getBoundingClientRect()?.top;
        const offsetPosition = nodePosition + window?.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } catch (e) {
      console.log(e);
    }
  }, [selectedDate, windowHash]);

  const handleRadioBtnChange = date => {
    setSelectedDate(date);
    setShowDateDialog(false);
    history.replaceState(null, null, ' ');
  };
  const getPastRewardsAndLeaderBoard = (slotInfo: any[], currentUser): JSX.Element => {
    return (
      <div className={cx('leaderboard-container')}>
        {slotInfo.map(
          (slotData: { slot: any; offer: any; userList: any }, index: React.Key | undefined) => {
            const { slot, offer, userList } = slotData;
            const { date, startTime, endTime } = slot;
            const { bannerImageURL, description, label } = offer;
            return (
              <>
                <div className={cx('reward-info')} key={index} id={`date-${date}`}>
                  <CircleCard
                    image={bannerImageURL}
                    alt={label}
                    date={`${moment(date).format('MMMM Do')} : ${moment(startTime).format(
                      'h A'
                    )} - ${moment(endTime).format('h A')}`}
                    label={description}
                    price={label}
                  />
                </div>
                <div className={cx('leaderboard-data')}>
                  <LeaderBoard
                    list={userList}
                    currentUser={currentUser}
                    shopMoreUrl={shopMoreUrl}
                    showWinner
                    saleType="POST_SALE"
                  />
                </div>
              </>
            );
          }
        )}
      </div>
    );
  };

  const getActiveRewardsAndLeaderBoard = (): JSX.Element => {
    const { slot, offer, userList } = activeSlot;
    const { bannerImageURL, description, label } = offer;
    const { date, startTime, endTime } = slot;
    return (
      <div className={cx('leaderboard-container')} id={`date-${date}`}>
        <div className={cx('reward-info')}>
          <CircleCard
            image={bannerImageURL}
            alt={label}
            date={`${moment(date).format('MMMM Do')} : ${moment(startTime).format(
              'h A'
            )} - ${moment(endTime).format('h A')}`}
            label={description}
            price={label}
          />
        </div>
        <div className={cx('leaderboard-data')}>
          <LeaderBoard
            list={userList}
            currentUser={currentUser}
            shopMoreUrl={shopMoreUrl}
            eventEndsIn={endTime}
            highlightUser={saleType === 'ACTIVE_SALE'}
            saleType={saleType}
          />
        </div>
      </div>
    );
  };
  const getDialog = (): JSX.Element => {
    const labelTextProps = useMemo(() => ({ color: 'Nero', weight: 'bold', variant: 'T5' }), []);
    const options = dataForDateDropdown?.reduce((acc: [], currItem: { value: {} }) => {
      if (currItem?.label === 'Today') {
        currItem.label = (
          <>
            <span className={cx('static-text-today')}>{currItem.label}</span>
            <span className={cx('static-text')}>Now happening</span>
          </>
        );
      }
      acc.push(currItem);
      return acc;
    }, []);
    const legendComp = (
      <div className={cx('modal-header')}>
        <Text variant="T5" weight="bold" color="Nero">
          {SELECT_SALE_DAY}
        </Text>
      </div>
    );
    if (platform === 'website') {
      return (
        <Dialog showDialog={showDateDialog} type="modal" onCancel={() => setShowDateDialog(false)} ariaLabel="Date">
          <div className={cx('date-container')}>
            <div className={cx('modal-content')} id="date-dropdown">
              <RadioGroup
                htmlName="date"
                options={options}
                labelTextProps={labelTextProps}
                checkedValue={selectedDate}
                onChange={handleRadioBtnChange}
                legend={legendComp}
                groupGutter="ts-web"
                radioGutter="lg"
                layout="vertical"
              />
              <div className={cx('close-modal-icon')}>
                <Icon
                  icon="icon-Close"
                  iconClass="top-shopper-close"
                  onClick={() => setShowDateDialog(false)}
                  ariaLabel="close"
                />
              </div>
            </div>
          </div>
        </Dialog>
      );
    }
    return (
      <Dialog showDialog={showDateDialog} type="drawer" onCancel={() => setShowDateDialog(false)} ariaLabel="Date">
        <div className={cx('date-container')}>
          <div className={cx('modal-content')} id="date-dropdown">
            <RadioGroup
              htmlName="date"
              options={options}
              labelTextProps={labelTextProps}
              checkedValue={selectedDate}
              onChange={handleRadioBtnChange}
              legend={legendComp}
              groupGutter="top-shopper-rb"
              radioGutter="top-shopper-rb"
              layout="vertical"
            />
          </div>
        </div>
      </Dialog>
    );
  };
  const getTnCDialog = (): JSX.Element => {
    return (
      <Dialog
        type="modal"
        showDialog={showTnCDialog}
        fullWidth={platform !== 'website'}
        onCancel={() => setShowTnCDialog(false)}
        ariaLabel="Terms & Conditions"
      >
        <div className={cx('tnc-container')}>
          <ModalContent
            modalHeader="Terms & Conditions"
            list={TncListData}
            closeModal={() => setShowTnCDialog(false)}
            platform={platform}
            label="Okay"
          />
        </div>
      </Dialog>
    );
  };

  const getLeaderBoardData = () => {
    if (saleType === 'PRE_SALE') {
      return (
        <div className={cx('events')} id="upcoming">
          {upcomingSlots.length > 1
            ? getEventTypeUI('Upcoming', 'Events')
            : getEventTypeUI('Upcoming', 'Event')}
          <UpcomingEvent variant="stack" data={upcomingSlots} />
        </div>
      );
    }
    if (saleType === 'ACTIVE_SALE') {
      return (
        <>
          {pastSlots.length > 0 ? (
            <div className={cx('events')}>
              {pastSlots.length > 1
                ? getEventTypeUI('Past', 'Events')
                : getEventTypeUI('Past', 'Event')}
              {getPastRewardsAndLeaderBoard(pastSlots, currentUser)}
            </div>
          ) : null}
          <div className={cx('events')}>
            {getEventTypeUI('Active', 'Event')}
            {getActiveRewardsAndLeaderBoard()}
          </div>
          {upcomingSlots.length > 0 ? (
            <div className={cx('events')} id="upcoming">
              {upcomingSlots.length > 1
                ? getEventTypeUI('Upcoming', 'Events')
                : getEventTypeUI('Upcoming', 'Event')}
              <UpcomingEvent variant="stack" data={upcomingSlots} />
            </div>
          ) : null}
        </>
      );
    }
    if (saleType === 'POST_SALE') {
      return (
        <div className={cx('events')} id="yesterday">
          {pastSlots.length > 1
            ? getEventTypeUI('Past', 'Events')
            : getEventTypeUI('Past', 'Event')}
          {getPastRewardsAndLeaderBoard(pastSlots, currentUser)}
        </div>
      );
    }
  };

  return (
    <div className={cx('events-winners-wrapper', platform)} id={platform}>
      <div className={cx('events-and-winners-container')} id="events-and-winners">
        <EventHeader
          platform={platform}
          openDatePopupCB={setShowDateDialog}
          openTnCDialog={setShowTnCDialog}
          middleImg={bannerImageURL}
          headerBtnLabel={getDayString(selectedDate)}
          EventHeaderText={description}
          onClick={onGoBack}
          className={platform}
        />
        <div className={cx('events-winners')} id="events-winners">
          {getLeaderBoardData()}
        </div>
      </div>
      {getDialog()}
      {getTnCDialog()}
    </div>
  );
};

export default connector(EventsAndWinnersPage);
